package uz.ferro.shop.pages.admin.debt

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import mui.material.CircularProgress
import mui.material.FormControlVariant
import mui.material.MenuItem
import mui.material.Paper
import mui.material.Select
import mui.material.Size
import mui.material.TextField
import mui.system.Box
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.events.ChangeEvent
import react.dom.onChange
import react.useEffectOnce
import react.useState
import uz.ferro.shop.pages.admin.debt.model.CrmSalesPoint
import uz.ferro.shop.pages.admin.debt.model.CustomerDebt
import uz.ferro.shop.pages.admin.debt.model.DebtFilter
import uz.ferro.shop.ui.AppTextMedium
import uz.ferro.shop.ui.HorizontalLayout
import uz.ferro.shop.ui.VerticalLayout
import uz.ferro.shop.ui.table.CellItem
import uz.ferro.shop.ui.table.HeaderItem
import uz.ferro.shop.ui.table.RowItem
import uz.ferro.shop.ui.table.TableView
import uz.ferro.shop.util.formatCents
import uz.ferro.shop.util.formatYMD
import web.cssom.AlignItems
import web.cssom.JustifyContent
import web.cssom.Overflow
import web.cssom.Position
import web.cssom.TextAlign
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import web.html.HTMLInputElement
import web.html.InputType
import kotlin.js.Date

val AdminDebtList = FC<Props> {

    var loading by useState(false)
    var filter = DebtFilter(
        dueDate = getDefaultDate(),
        minSaldo = 1.0
    )

    var filterState by useState(filter)

    var data by useState(listOf<CustomerDebt>())
    var searchQuery by useState("")
    var salesPoints by useState(listOf<CrmSalesPoint>())

    val filteredData: List<CustomerDebt> = if (searchQuery.isNotBlank()) {
        data.filter {
            it.businessPartnerId.lowercase().contains(searchQuery.trim().lowercase()) ||
                    it.businessPartnerName.orEmpty().lowercase().contains(searchQuery.trim().lowercase())
        }
    } else data

    var selectedItem by useState<CustomerDebt>()
    var showDetailsDialog by useState(false)

    suspend fun loadData() {
        loading = true
        try {
            val pts = DebtManager.getSalesPoints()
            val current = pts.firstOrNull { it.id == "Distribyutsiya" } ?: pts.firstOrNull()
            if (current != null || filter.salesPointId != current?.id) {
                filter = filter.copy(salesPointId = current?.id)
            }
            filterState = filter
            salesPoints = pts
        } catch (e: Exception) {
            console.error(e)
        }

        try {
            data = DebtManager.getDebtList(filter)
        } catch (e: Exception) {
            console.error(e)
        }
        loading = false
    }

    fun refreshData() {
        MainScope().launch {
            loading = true
            try {
                data = DebtManager.getDebtList(filter)
            } catch (e: Exception) {
                console.error(e)
            }
            loading = false
        }
    }

    useEffectOnce {
        MainScope().launch {
            loadData()
        }
    }

    VerticalLayout {
        sx {
            padding = 15.px
            height = 100.pct
        }

        HorizontalLayout {
            space = 20.px
            sx {
                alignItems = AlignItems.center
            }
            AppTextMedium {
                sx {
                    fontSize = 20.px
                }

                text = "Qarzdorlik"
            }

            Box {
                sx {
                    width = 100.px
                }
            }

            TextField {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                }
                placeholder = "Qidiruv"
                size = Size.small
                variant = FormControlVariant.outlined
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    searchQuery = event.target.value
                }
            }

            TextField {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                }
                value = filterState.dueDate.orEmpty()
                placeholder = "Sana"
                size = Size.small
                variant = FormControlVariant.outlined
                type = InputType.date
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    val value = event.target.value
                    filter = filter.copy(
                        dueDate = value
                    )
                    filterState = filter
                    refreshData()
                }
            }

            Select {
                value = filterState.salesPointId.orEmpty()
                label = ReactNode("Sotuv joyi")
                size = Size.small

                sx {
                    width = 200.px
                }

                onChange = { evt, _ ->
                    val salesPointId = evt.target.value
                    filter = filter.copy(salesPointId = salesPointId)
                    filterState = filter
                    refreshData()
                }

                salesPoints.forEach { sp ->
                    MenuItem {
                        value = sp.id
                        +sp.id
                    }
                }
            }
        }

        if (loading.not() && data.isNotEmpty()) {
            Paper {
                sx {
                    width = 100.pct
                    marginTop = 16.px
                    overflow = Overflow.scroll
                    position = Position.relative
                    flexGrow = number(1.0)
                }

                TableView {
                    headers = listOf(
                        HeaderItem(title = "Mijoz Kodi", width = 150.px),
                        HeaderItem("Mijoz"),
                        HeaderItem("Saldo"),
                        HeaderItem("Muddati kelgan")
                    )
                    items = filteredData.map { item ->
                        RowItem(
                            key = item.businessPartnerId,
                            cells = listOf(
                                CellItem(item.businessPartnerId),
                                CellItem(item.businessPartnerName),
                                CellItem(title = item.saldo.formatCents(), textAlign = TextAlign.end),
                                CellItem(title = item.expiredAmount.formatCents(), textAlign = TextAlign.end)
                            ),
                            onItemClick = {
                                selectedItem = item
                                showDetailsDialog = true
                            }
                        )
                    }
                }
            }
        }

        if (loading) {
            HorizontalLayout {
                sx {
                    height = 200.px
                    alignItems = AlignItems.center
                    justifyContent = JustifyContent.center
                }
                CircularProgress()
            }
        }
    }

    if (selectedItem != null) {
        DebtDetailsDialog {
            isOpen = showDetailsDialog
            customerDebt = selectedItem!!
            onClose = {
                selectedItem = null
                showDetailsDialog = false
            }
        }
    }
}

private fun getDefaultDate(): String {
    return Date().formatYMD()
}